import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@shopify/react-i18n';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import SalesChart from '../Tracking/components/SalesChart';
import enTranslations from './translations/en.json';
import frTranslations from './translations/fr.json';
import TagHelpModal from '../../components/TagHelpModal';

const translationsByLocale = { en: enTranslations, fr: frTranslations };

export default function Dashboard() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();
  const [i18n] = useI18n({
    id: 'Dashboard',
    fallback: enTranslations,
    translations(locale) {
      return translationsByLocale[locale] ?? enTranslations;
    },
  });

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [productCount, setProductCount] = useState(null);
  const [confirmedSalesCount, setConfirmedSalesCount] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const [statusRes, countRes, confirmedRes] = await Promise.all([
        authFetch('/api/status'),
        authFetch('/api/products/count'),
        authFetch('/api/sales?status=confirmed&limit=1'),
      ]);
      if (!statusRes.ok) throw new Error(await statusRes.text());
      setStatus(await statusRes.json());
      if (countRes.ok) {
        const { count } = await countRes.json();
        setProductCount(count);
      }
      if (confirmedRes.ok) {
        const confirmedData = await confirmedRes.json();
        setConfirmedSalesCount(confirmedData.totalConverted ?? 0);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const syncOrders = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await authFetch('/api/sync-orders', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Sync failed');
      setSyncResult(data);
      loadStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  }, [authFetch, loadStatus]);

  const connectedSince = status?.connectedSince
    ? new Date(status.connectedSince).toLocaleDateString(i18n.locale)
    : null;

  return (
    <s-page heading={i18n.translate('Dashboard.title')}>
      <style>{`
        .dashboard-grid {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 16px;
          align-items: stretch;
          margin-bottom: 16px;
        }
        @media (max-width: 768px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      {productCount === 0 && (
        <s-banner
          tone="warning"
          heading={i18n.translate('Dashboard.products.noTagWarning')}
        >
          <s-stack direction="block" gap="base">
            <p>{i18n.translate('Dashboard.products.noTagWarningDescription')}</p>
            <s-button variant="primary" onClick={() => navigate('/feed')}>
              {i18n.translate('Dashboard.products.viewButton')}
            </s-button>
          </s-stack>
        </s-banner>
      )}

      {error && (
        <s-banner
          tone="critical"
          heading={error}
          dismissible
          onDismiss={() => setError(null)}
        />
      )}

      {loading ? (
        <s-section>
          <s-stack direction="inline" justifyContent="center">
            <s-spinner />
          </s-stack>
        </s-section>
      ) : (
        <>
          <s-section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong style={{ fontSize: '13px', fontWeight: '600' }}>
                {i18n.translate('Dashboard.connection.title')}
              </strong>
              <s-badge tone={status?.connected ? 'success' : 'critical'}>
                {status?.connected
                  ? i18n.translate('Dashboard.connection.connected')
                  : i18n.translate('Dashboard.connection.disconnected')}
              </s-badge>
            </div>
            {connectedSince && (
              <s-text tone="subdued">
                {i18n.translate('Dashboard.connection.since', { date: connectedSince })}
              </s-text>
            )}
          </s-section>

          <div className="dashboard-grid">
            <s-section heading={i18n.translate('Dashboard.sales.title')} style={{ height: '100%', boxSizing: 'border-box' }}>
              <s-stack direction="block" gap="base">
                 <s-text tone="subdued">
                  {i18n.translate('Dashboard.sales.subtitle')}
                </s-text>
                <span style={{ fontSize: '48px', lineHeight: '1' }}>{confirmedSalesCount ?? 0}</span>
                {syncResult && (
                  <s-text tone="success">
                    {i18n.translate('Dashboard.sales.syncResult', {
                      recorded: syncResult.synced,
                      total: syncResult.total,
                    })}
                  </s-text>
                )}
                <s-button variant="primary" onClick={() => navigate('/tracking')}>
                  {i18n.translate('Dashboard.sales.viewButton')}
                </s-button>
              </s-stack>
            </s-section>

            <SalesChart defaultRange="last12months" defaultGranularity="month" compact title={i18n.translate('Dashboard.sales.chartTitle')} />
          </div>

          <s-section heading={i18n.translate('Dashboard.products.title')}>
            <s-stack direction="block" gap="base">
              <s-text tone="subdued">
                {productCount !== null
                  ? i18n.translate('Dashboard.products.subtitle', { count: productCount })
                  : i18n.translate('Dashboard.products.subtitleLoading')}
              </s-text>
              <s-button variant="primary" onClick={() => navigate('/feed')}>
                {i18n.translate('Dashboard.products.viewButton')}
              </s-button>
            </s-stack>
          </s-section>
        </>
      )}
    </s-page>
  );
}
